/**
 * Genera el HTML autonomo del PDF "Informe de experiencia del cliente" (EVOLTIS).
 *
 * Fuente de verdad del diseno: specs/007-pdf-resumen-cx/template.html -- el CSS
 * (REPORT_CSS) y los dos SVG de marca son copia verbatim de ese archivo
 * (extraidos automaticamente, no retipeados a mano). Si el diseno cambia, se
 * cambian los dos.
 *
 * Funcion pura: no toca el DOM ni lee estado global. El llamador
 * (SummaryDetail.js) es responsable de abrir la ventana, escribir el HTML e
 * imprimir.
 */

const REPORT_CSS = `/* ==========================================================================
   1. TOKENS
   ========================================================================== */
:root {
  /* Paleta oficial del brandbook */
  --violet:        #5351FB;
  --violet-deep:   #2C2F88;
  --violet-light:  #A9A8FD;
  --magenta:       #7F1D98;
  --ink:           #282938;
  --paper:         #FCFCFC;

  /* Derivados: jerarquía de texto y filetes */
  --ink-2:         #5A5C6E;   /* prosa secundaria */
  --ink-3:         #8D8FA0;   /* rótulos de dato */
  --rule:          #E2E3EA;
  --track:         #EDEDF6;   /* fondo de las barras de emociones */

  /* Satisfacción: escala divergente en colores de marca.
     "Sin dato" no lleva relleno — la ausencia se dibuja como ausencia. */
  --sat-positiva:  var(--violet);
  --sat-neutra:    var(--violet-light);
  --sat-negativa:  var(--magenta);

  /* Tipografía — idéntica a eva-ui */
  --sans: 'IBM Plex Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  --mono: 'IBM Plex Mono', ui-monospace, Menlo, Consolas, monospace;

  /* Retícula A4: 210 − 20 − 20 = 170mm útiles. La prosa se mide a 128mm
     (≈70 caracteres); el resto es margen deliberado, no espacio sobrante. */
  --pad-x:      20mm;
  --pad-top:    18mm;
  --pad-bottom: 24mm;
  --measure:    128mm;

  --wm: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAcQAAAClCAYAAADCgI1UAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAOdEVYdFNvZnR3YXJlAEZpZ21hnrGWYwAALxZJREFUeAHtnVt627YSx2dA2T3nqe4KoqygzgqqrKCy3T7HWUGcFcRZQZwVRHluYisriLKCOCuIuoK6T6e1RODMAKREyRJFgheR0vy+z4kvFC8ggBn8MRggVEC/b44ApscBmJ81wmOF6kcA+p2BI2gABuBIm87JcIjj+Henp5NXaKAHOTAIt9fXBy9hB+j3/9cNVOdN3nekjHn5x/DwFhoA1buugkmP3kwXVfAIjOny9wDIz7T8XHf0N/qiOoBwZ7T+piGg59DjYUOeJwu53xviONTBS6r7d1AzZ2fTZ6DNOQgW6j9G1H+8Xvf3s7P7F6CxD3mh+kyfG34cdt7DHsP9AUB4rEAfzfoExvYLCVx5vafyGiKUQGwAEU1fAfxKV+hCwzFGv74e/nDJ3/f7k16A8Bk8ICP69MPwYAQt5+zkfkBP8wxyoo15ezM8vIAtwPUugMkzUOqYXih3HGU5XHfUSEbcqYQQfEk6Tk3jpH9/pRBf5PmMMeHL6+F/rqBmqI59b0PfUCeh6fy0yjkp0ifF7ErflBVbZjQIA4U9cIObPP3BHb2Lxx0oAN+AM4LTZ+7ipdjX2jmgkYL2vPcQbEc8gvbzC7SAmRFE9pynPVvnjIGSYTWjTz1KP4ApnJ5MbtHgVTONo/Fod7glpQa7ICzD76KS0bqGpZHQDjK3QTi3QX7dgVWRvAxi5L28om97bTWCSVjyOzuZjADySaYMeef8IrYyQiqLs/60D56Nh+reAGqg378nBQLOnfNVb4dONfyYjOOAjePZ6XQQavN+uEeetyA0japsUC6DyN65UlOea2u1AVgFSahfEFUP8nP0G72cVksTqPt+lQppvu2g0vm2xYrfAIw5p/s5ZwcqNPBaDKMg1EfV/UFmg+huZPKOZoK7sINo0DQCUK/Ag/bLpvgreKCN/gQVYaVRnPA8V+55zZrghtk7O50MQ9152eR5RkFoOzZ4DDvvoGLHWGU5iCMw3QQvdmFHGQ7/OwZPoxbJpq3EyaV+wShVyaUcXRfg9LtPkE/t0Fwj3yu1kTcuuEwQhDLh6GQyhl+hBpVoo0GMliNcwh7Asin4YWVTaCNWLvX6YOnLE9gLJCnyM2jkkWGrjAtPIwQYfv2d5jpBEIRSYEeTpKgB1NQfqA03szfGkGHZFDyJZNNW4UY0fqOwsuXSOr3A6jBdjfiV2w0IguAN903sHNcdr7LWIO6bMWT2TTZVMPE24mXKpXV7gVXD7YbmFm9EQhWE/NjgTZzyGswe1MxKg8jzSvtmDGOMCX1HPq2TTVH5BdOUJZduywusBTu3GH512TIEQchCbAztUqctoB7e0P+6NK/0BvYUDT8MwJM2yaZ29MKLzz0gp6FwSigXNTZpuUS6CUPPGH4WoygI2dimMbTXX/5FgMHlPmeUiNIojcCDNsmmReRSDYcDKIAzhsFORy3PEaMoCFngqZNtGkNmYR2iHR22IdS9OHcB4HDdH1k2RQx6kJ/WLNK3cqlHiiP6yG2RNXc1GsM7Y2CMCsZ0rTujwz+Tf4yTf9MxR4hVN8KZUXy61+sVjXkPLV6iVDZF29IuEcWsbHHqhKeBcLxgEKOFj6VBnc2tAfOFrP6togsimLsJHNSeZX8Fdx9Tsv2zbBrA1Es2bsMifev4eMqlUEAurdwYGi53MyTZ40veHTjYkTGcGNjmSK1CxhWj+HF4eE7Pfwk1wLueIGLu/owTn1P7H0INiDF0cL9QS8yKMWNDzjENBmzfkHSSY9VrZhB5/ZQupyO4o4d7O4XO1Ta2mCkDvu8iuU250Tf52RV4jX4tRToLhZ0b+q8LpUKOFo08ita3aFTPX2+5gXIZoU0RhV0ojZlRfNLWtlGUuoxAvz8ZB+CDuhNDVS/OSS6dO3KQb9lBRsBv1D/cZmlzM4OosYThKgKnsXq+C429iGzagSlLcCNoKOgpWxWReOz8gClRmqTRICkPrz8MOyMomWj5zYC/Tvv35+UaRjKKasojlxMQhD3Hrtk1JTrJViXCtyEEIx87ZA1itJ9hIW2fOrvXH24OLmFHKCKbGmUNzggaiJsn9lQCPOVSt9FpafMD5PnhRV2bn14PrZRSrmEkufq0/8/FNvYkFISmUKpUOnOQi8VvKPePf8QhY43hcHeMIVMk2pQ3q23qouy65VJrgF0qtuKQFM+beG5jJ3A2jKEJn9rAkBIg9eFNa9P9CUIJuBUNxWH783F4UMpmyNYg+i/QBiuT7poxjCmySD+STRtH3XJpSfMDd8aY51TpL7Ypx7OUyoEhNGf5EkrY1NUgvpNsNsI+UtaKBu4XyrQ/bh2i8Q+m4a1vYEcpskg/kk0bRd1yqcvpiV0oBI5pVPgkki4bwYfh4VVopk+oUMZQCNPl/UVBEPaMEkaHHLz5tOx+QUXZ+f28VMTBLkdk7ZpsWqdcWs78ABvDoJHLFHi0aCXUgkaR117JDhnCPlHC6PBOGfO0ivXeSoPqgieoTe1zOXVj/OeMjorOzZZNnXJptMSiAM01hjFlGUWNuLepEoX9o4hjbjH4/I+St56LIYOoe+AFjtuQkaUoGg54ZOQ1X4QNyspRp1xqIzILpWBqvjGMKcko9iTARtgbEF+AJy6AplNZ4gSlFD4CH0z4DfaASDb19UaOmyKb1imXuuUJvrTHGMbMjaJ/oI0pVGaC0A7sVIqns8yZz6oO4FSkh3l12AbMCPaEXZBN65JLeXRYJJAGjXnexnlpu5ifpBzwR0aJws6jAAtsKtCpPJmF4gTH4AHNEX2HPaHtsmkRuRRNvjWERUaH0XrWEbQUK+UYeAueyChR2HUQA78lfjUFcJLBRj9Jz5i/YU9ou2xaxCsLIfiS9dhio0M7J30JLSeEzmWB+UQZJQo7S9QP9sCDUGtvRzMPvA7Rq7Oegml9vtI88OgF/Ni+bIqB3yjVwCiPV1ZkNMzzhrADsPOEBaRTo8A74EAQmo1fshI3bVNNVOkyCrxpxDZOtcHZ0qGFsmmRSWy680HWIwtFse7YelYr+/rOOxvoSfYaYRdBtzVefoz2zRiWmwIGcb9gz1/7v5itdXJ1yaX+mSdwHOrAd/TdWEIIL8EPUhT+PQdB2DEU4s/ggQI1gpoQg5iDANQAPNlWJ4eo/CS4nHIp8Qt4wHsZ7mK2Ixd16jdK9A48EIRm46NU3dUZaCcGMQfFZNP6OzmXEgy74EUeuZQDQfyuQ2U6gB2lwChRZFNhp4jqc/46baCWucMYMYg5aJtsOgU4B0/yyKXecwM7nwvXbjQ8Ag+alvZPEIrhF1CjwdSaAEYMYk7aJJsqBL9Raf7oUj+5dA9y4fpGJ6NSXmUqCE1EQehlEBH0GGpEDGJOIj278bJpfXKpbxTrfuTC9ZbZjZERorBD+K13VxCIZNp0tH8qt9pk07rk0gAO/KSQGkOpt0kBmf2I6koXBGEHIMXDK8K07vXuYhA9CACH4EldsmldcqnvbilFyrBtIOAIPKB5xB4Iwi7gmTO7rgX5MWIQPWi6bFok6jOPXMp4ri262we5NCbKhZsbU2gLLUFoEH45s2tP/iIG0ZMmy6YBmnPwJI9cGpG/0zY67zVajc2Fa/LnN1UgkqmwI3jlzEYxiG2h4bKpX4RiTrnUd23RPm0dFqPB5HcCMPCadxGEBuKxBrHQpttedMCTQE3fnJ1Oa7fgedBG/23MwVUVa91Y8js7mfDz537RkWx6BRXg5FLoghf55FLftUV1R441AQTDz5wzp62MEIX24xznKbQBb4NIbn7fznI0GEXdEMB9l/6pJISdZVOaQ/NJjWZl02hbqVJxcimCD3nl0sCGUuevA/u2UwqD0Bn7lBVHmu5y8gJhH/jnqIipqZM9kEzVj1ARDZVNa5FL3Uf8FtvWHTnWBEKYeD7zpJZlOoLQOFT9jqDMIRagadGmdUaX+rOvo53/eNWTjueCZkFoDp0utAQxiAUpGG3ahRKpObqUBcAu5GULE+VNwFce1zKPKAi1IQaxIMVk0/tzKJfa5FLBgz11BgShLYhBLEgx2bS8BM7tkEsFQRCaixjEEjBGvwU/SpNN65ZLBUEQdg0xiCWgQI3AkxJlU5FLBUEQCiAGsQSsbOo5P1SGbHrWn/ZFLm0BKBGjgtBkxCCWhAH/aFO3d2EBUHsnHqhdLkXswv6S2yCqvV2mIuw7Wuu/oWbEIJZEEdk0hKKbwWItWz09uCrAGHJj9nKUxBspgyDsIYHnWlrcQkYrMYglUUw2DXLmuJzj5FLwNDJF5VIvY3pU1ybJzcJvcfI+prkTdgsDujXtfecNIo1iapME/WVT0/2t77kZ7BblUgTl2VlPurBnBN4ZZw7GIAhCLXhnXEUDT6c2YXGjuasigfY6NOhBAOoVeBDtPD/K8ZE4i7ynXKo/FY0uVaDH2iORuHJ7KO5VPlN+vyp/WdVafwWhGnhpmc+GA/XPn3sbRDaGEq6/yHD43/HZyWRE3/YgJ5FsepnnMwomHF3qOfIIbqAgExq9BB7buuzjTvAKMf/ehma/nAZB2DYyh1gyxns3+PyyKSr0ThBOcuknKIjvTvCIWFqGnhaR2wnQYL6BILQc47k/6zYirMUglgzLpuBJJJtmwsqlxnOfRyeXliPFKcw9iiHx5HifAmuiTZvzbyRNkjQIQstRqB5BSxCDWDIsm0LOucAYRJV5s2Enl/pSXC6NMTr0GhEXu/92oTz3jVQQiGQqtB7jGQW/jRgVMYgV4C+bwlFW2XTbcmmMb6eNSu2PbOq5rCZKHC8IrQY9JdNtxKiIQawADYdX4EmWRfp2kXcT5FKwXhwbxPznM6a/D7Ipvyv0CSIyfiqDIDSJqI17tPPtBGyKQayAyOCMwAOFuHE0QaOyHnhTnlzKRM/qM0o8UvDvOew4AQaX4IGBsLRRvCBsj6lfRPmW9g4Vg1gRxnh3aBtlU8xgNNdRplwa4/usiIG37NsivKRhXSAVoCA0Bd/5821FWItBrAgNPwzAkzTZNMqJ2QMfSpZLYwo8a887Q08LOO3z1l7YhdzgeDg8lIAaofWgCrwcwm1FWItBrIiqZNMmyaUxRZ7VIHhl9mkD6PlsmhwXEIRdwPg579uKsBaDWCFVyKZNk0tjCjzrTo4S/UeH3IcceAdlCUJT8F1/y0TBerUjBrFCypZNmyiXxkTP6nX+XRsl2shSz2cyALeSElHYBdBzW7uoDWwlh28BgzjZm0wjvpQtmzZRLo3hZ9XGf5Pk0/4/F7AjKNV54Ts6RIMyOhR2AoXgFTRHKtjW5s/ZIHpZ4o53Uun9okzZtIBcevdx2PE1VpkJAIfgCWLwqt/nrPjthmUiNOBp3HFcx3sy0naFinH7tGIXfNDbW4OrqMf2Moga2t951UFZsmkhuRT9DVUeoswqI/DjSOG00lFs1fA7CtC8A0/QY4SNAGPIiVLqRxCEKkGTOQ3lMkX3aS2CMuin1e7jFj4+lCWbFpFLacRSi0GMrvUaPOGMLqenkzfQUgLVeePtFdPocAqdAeQmv0NrjJG2K1RG344O/Zz3bc+hK2O01wJIr/3d9pQisunZ6eTmrH//Av0DT+4+3HRqC+MvOEpkg3rRxvlEMuSvvNPpgRsd+nUEKrdB9M0tKQhZCFD7O7UmrHzKIA2aQ/SO5tmrLXyKUCQC03ayyIEW2AUfapJLk5CX9RIKQPOJb87Opt7LS+qGjSEZ8kvwxo4OvYJpAs+0ebucEEHYHtYxBOyCJ9RX1t5fJVHo16CYvchFWQYF8n0Wpk65NOYPzrJi4C0UQZtBG4xicWMI7PRc+oaZT+BgDB7k2XtTELJwdkZKVpG2YGC07SVHSkM4Ak/2JBdlKRj/JQlFqFUuTRJC5xJ8R8UxZBSdx9lMyjCGxsBtkchSa0g9EiHn2XtTEDbxe//+GDReQiFwAFtGRRva+nZcPRdeK2xCwwGP1OpdbLoFuTTGddT4HArCBqdpRpGnCs5Op+8KjwyB60XnBAqiwfhE5R1J2xXKgI2hRvwMnllpHPUsOdqEXZhfKHcimne7sH6sarYhm25DLk1CFXwIJYyM2fCcnUy/N6Ge8TrDACdf6bnOoSAckVuGRISAI/AB4Y3EAQhF4GmN4sYQ7LQBNABrEANQA/DHrh+ThrWZmmXTrcmlSUI4uKAnH0NhTDfA6ddtRaBy/eYlIQHC5yJBAzNovuTD8OASSiBSH3xuohuosLXLXITtEbcHntaAosawIaNDxhpE713PI3j9WIDhVxkpplOrbLpFuTQJj4yVAZYFy3juIxuBSqNFkvtqC7jhRN08KvTPQLMMjmmOtbCcHFNkrSuPdFn+BUHISOntwUChqPQysQaxYB7KCPbgw891dlRtw5VzPVv7bFsuTcJRp2iM94L9h5DjhWZQpWG084T9+xdnJ/ffEfFdKaPCCGX0SdnRdAXWulqjeHoyEYdWWEs0Inx1djL5q9T2gDiwUysNoRN/w3koDUDByDPuqIA7qksN+pMxMJCNThdheZrKuWqnoRFyaZIPw8Or3/qTo3J3tpjXN/p/FGpe3G4TA3jhZP/pcaDwGRibi7H0aQByDF7+UUGb4LWuAUy5bL3u2ak85GCcTgdFy1FoP+wcKZj0qL0e0xz1L0jtgtPIlAspJToo0VEuDiZ/IOvPk6M9KBcXTII4Njr8ExoAQvB9W5o1d7rc8UBh3T0F9rquy5PkyoRGXQMosKdjBmx902C+GaNGLrVZZ8y/j9f6RfPdbPyo0ZsuN3oFNvNSDyqEg2jKmjdcxWn/30tEVZbDMStHtPmOq18fRp3RjzTn/KmM0bMLfuL53nzQPP/z6+HhALaM7/0nWX4WjirWqHsKV+SyNdQOeEoCuG1gF+rA4EmTRodMJ/kDZxjRiF+hXLjz6fGCK2qs0AwMVw7YhlHkTvmk/+8nqpSVGQXUW1nzmImPw8NzMopQoVG09Y0MXG+eYHhq/yWHD5I/O3DRK6yIqo0ho+HwikaJ/MxlOFuJcqyjhBx0/5f0308gFEYtOTFsfH7v349p2uZiVfvDxL9V49pDs4whs2ChSskw0hIMhI9hSxSM6t0Ajj80XO5iowimuUa7bOowhgw7W8botrdfiVavEO7jQwgvYZtQ26+jPfjwYMjmMoyUESYvrKNoVG8quL29xPLARrHIzhhtoS5jGMOjRGm//pBy8xfsCFMw1fQxBeDdLKxD3FAeGMREmLxQEeVE9a6myXLpMmwodtgo3vEcTt2eMNctLCFD0N5izN+wIzQtoJGNoTadp9BgVk7qRWHyjVkbsosU2V1+Pc2XS5exBoMm13drVINjmo9/uq3gDK4D+zD6roJIvWk9ZkubCazFwIiNoW8S+7pYG+XCYfLSqKojMlzlVo6WyKXL8GR/aMKnjWvEPtDIPzTBkz+27J07R2N/5mlL4q7pHXZWELE5bYnq4cfhQeONIZMa9rnjktbWKVs2bZNcugwnmb++OXjS4vp258LID8+b0vD3LXipMGYHHLIY3QznmJXGJs8ZLrNxHYQziiKfVkG5smn75NJVcH0LzfRxqzpyA29D03nctDVVjDOK+xE5XhQDYaOSWRQhhMBnB5QSwTE5t09ZaYQWkWlhID+U7aQkeq1UIgM2gjJoSLb4MuDRInfkHJTS6DpH8yLc6EkOumiyHMT3J0rPZra9W3tpIA62uNHuHdc1njZoo4OeeaW87aRuDh9LwyoXcjQ4IrBQZ8rvpCnZ4suEg1K4zrFhNE2Ss+aG8GlbGv1s5C1O7Wq2a0RKZWvp0KgMSSlhQ3jZ1rnY3KljWilpNRh2NDgiEXyNYoMXuZYFG8broZ1ffLrFesdLZd62zRAmiZ3axo+8t0DTcmr6UtYemzmIRoSdnzhdZNudig54wA2L/jvv9/93qSDooU3YjF0QvOCIRCrLJwEGufba47ndtmn0RYgl5n7fXCiY9FHhrzRa60N13PE2WhysxOH4uxKBGC0HGfA2PuhSePVgj9mCEakGmiuuyTmetYtdiFtIUlriut/798ca4BcqKO6gjqHpKZgMnjdNZuSk0x2YXhg0z1IMo13Ub+DgalcknqL81p/0NGhyzNQvUKTuGRo1KbwFbUYI+G3XGvs6yBnrBhD82oy2izSK7RROq2j7owx5mevOJJQV+06w8z3j4Xe8vVpW5zjaYCB7Rh7bLtQItL7d9XZRWSbXqHMnI8l7rHEmdTxSakWW9S1Ans2f5PFfNdnj507egPmZnIzHttyiyrhLI5WqiOuesds36UdUjmuSReMYQd0p0OMJHIylXB3OuVXduOxQBY+gJpTWb8taw2lHwMo6SQ+I+oBGzxvy7hQ0qfVr6kHUL4Rw8D5v3V117vluRK5dhBDYFJPSLgRBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEKoBT/v/XkJBEILbj8POEIQFzvrTvoHwmL8PAId/DA9vYQ847f9zQbXiiL/XcHg1HOIdCMIe0u9PeoimrwB/NgBH1FmOrq8PXoLQSDqI6hUUBWFA/4pBXEJD2FeonvH3oTFj+m8vDCKCegGIXfuDsXVDDKKwd5yd3b+gTuCKWwRj/9XSFppMBwRBEDLAo50AzTv7A6rRx+vOcxDWo+Fi4WfrFIta0mQWDKIx+jW9sDHkRJvpGIQH0EjpL9cIAGik+BfsC0h1yBgQdhHs2v+06YKwln7/f91ZWQErRJ3HNHUwBqHRLBhEBWr0YXgwAqEUrod2rmDv5gs+3hw8BUEQInAsxrAdKBAEQRCqI1KJhObTujnEft8cKZj0qZZ1EfCvEPDb0GNUS+fp0nl68c/aenH+o2OeX1HA58Sx7yibn43EFTqPjiI0Fc03BLe+3qU73/SY78v3fMny5p+LlPnme4zfqfrT5z657ALQjwyYnxLnGZUd5frwPRWrO2US10P+vmj9KZN5PeL7Ohiueyfz+0/WBU3l6xehXbQNxPeNAD/O61V59X/VteK25u43uC3WB7jy5OceblgJsPiO1pfTw74TbvO8n+W2yiNobafqOuMifV187+6e/Noknp1MZpM9aOBpUcn0t/79hUF8M/uFwZN1SzLcJD18nl/fvPwwPLzi70/6/w7iCE2+ryl0bgOccMTWs4dn4jkruKTrvIcN8FIIOuEL+ra36jwGzeD6+uD12s+fTD7HnyVpEHmuIMDOu9n5DIw+Dp1kmHwGY8zz6+HhYNU53TmCS7r+r8Ch2Q9uCwehDl6vqyyn/ftzRHyXvM7p6eQVldtFyvlephmKB8/18CRry/ysf/89jjJdN3diK7Cavlh7j/ws1NDQ4FXae43Ow896vu48m8ovK+n3PC+P5PNzHUkelaw/m9obL4mKo8DT6096WWYpxzSS9XglVL5xgM1iXdSvr4c/XLpoS7yM7235ubPUhU1tPFlW8fmLtIHN9R/uDMLVcl+xsawS/cP8Whmef0MdTtYrbnMA/8Di/eP44w3//mG/xA5K1IYu1lzXllN6meA4NMHTtDaWoUyZEd3/87XPudS2Nt2TMdO318P/XEFGSpdMI4M2mv0CzTvnESzCv5tFrNnjcBAbw2UM4CMyhl9XG0N7RJeuY40ApEB/f0PH3cDaF0JeKTW6s5Ppd/aCYAORIfsM6S84ld/798f0QuNnW90YjDkPcPp90/NZlD7ixsHPseF8N7DxntKeK1uZr8KV2+Rr6j2CDVM/TrtG4jwXaedxzxt+XlUPs98zdVo4TSlX//IoQpayTJTjG6gZWx4ar9bdm+sHNteFuHzPTqfvYBOIP56ebDhnShvI2K6PXF8x+QwFyNoWEnW4CxvowDR7v0T9RVSvL9Zfd3qzuUxMN62NZetTLD3u687Ops82HJfhnNSfY/DGrYvORiVziKGZsrcYe15HgXpYidkjmUdhkXdB3s/aE1JD4GPZ02WPhj0g/mJPkF7Y+/lhcLnu4RPeYnzsa/48n0cZ84TPSwU4dn9Nf7kxgeq8iZ/BGLhlo25Af4GMcCXTiFzJ3HXIe9z0fJsqCppOPPq942fkETqfx54LF9aK9n47mf66+p7gZnZP1jPHk7R7+q0/l0+yEI2Gu/H5o/fwE3t8/H/Wayych8pu+TzL79TVOT9YnbCGxWHLlusNfyXv13Zs8RrMinHGhDup+Ho2uvci5V1d+BjFANTAjiSMeRv/jus7/87WVz1ZOWqjkeKzqKNn7mz9ozaCYGajskj16cb3H9V/+w75i8s3+QzcQW+qbyTDDaJ3tdAG+H9IOuvcBlbWKx5xRPdE98yfTd7PYr2CXjK5SVxWrHbBirKiE75dvNbi+0s+/6o6nMUo0sj11YN+Cc1w1bFogleurFzdmfUVyTK3Rso67XzOpTJNHscy9+Ry1XUW+pQVbdWeCxJzrdpcbep/43OyXUje+9L7occPXmV1hhfmEEMwfZI8upCDVXNmw+F/xySdvp5Jpwb6XPHi46xsSQ8wu2F6gE1yFj+0Np2nSxIHf2Z01p9wY3thz+UefpA8zmaLSDRMqtRPV2SNuSVjMJpXUNbJ7/keL1Nuqu86dPP8o4fUHHlcUSUx7z8OD8+XDhnz81HZjV0lh7iifFovd3JjWStfjJLSCp2T7h8+Ld3T5YKjYoInm8o8urcRZCCa03mWOP/CfUbXGkX3+ii+V+0y/oxWnwfuQuicJO8z+n7g3qn1Itmjv6DPvc47p8gyYEKdWFd/Ft9TDVgDb7DrforfVefBu6L7og7RTU1wGdDPn/JMjcTH9p3xcO2MyuHjGgl3jru35FTICn6Jv3F1oTNO/jEq5/NkfeN+CtLr21FKGxguyItL5+JRh07KjNedkxX3c0vH3VKH/NUehYrv65K/n5cVjaigY/u/dWVlR88GuvG1lt/fvA6boR3FWcNFRlGFXMfS1oD2cvRLnD1nSAOS50t1Z6H9wbxMl/sD6gumw0h54+Vl7GQvDEqietONn5Nk7wXJOG7z9JxPIiWw66434f9vYS10HPWb1w/7TYja/ff43mnUvNB/rGNhhKgQX7D+n+fLqNUy5rJ0SsbRSqd2fQ7qmZfK3kaWxknG8GRdR0ad4SUkRqQK/j1P/j1IdFJUSV6vS6HGhpzmWmYVLaroKbhG5zPvGnWy3fg8K4zhDDr/JczLcmHyeBWpWr5JeKhmUWqI1k49WzpPljI/hsxM58cijNIcIbPgfeKCh3fgGos7jjr+dffJ71QbnTD6889lhtrF/KbwYl39WXpPlcLvKql4pL0rWz8NzN57ZARqwbXv1cZwYa2eSa8LdNwo/k4p9SNsIK0NJOsVp1RL/k2D6s4vuf5dch0w8876KIuUuQz1ReeJ75+vr8NITlhi+RZ9btOIJ3u/xOpcZ+Vc6mL743PqlX1wCMEo8anu8t9VlL7SnXP6FtbA5yYV4sv8c5v6lfX9Jrd7SLRFnbiHNCpddkFDYfauogJ0ktXyCCTqRNLZ0Fi4IGkCf1bQqDqzSu4a3azjv0vxVC2u85gNt1MrOssQvoEaqHAuV5qUUWh8iAk/zT+rfll/YHpZhTBZ63FR59DPeh7bMIzhexqBHV1nnZ/r3MbSBkltr9OP1WvPOYFw1jDZc06T0W6GP5zHklfeaEVreBJS6abAFCsn1YCCoDe/KA421UNMpFaMvPga2NS+/3M3qwswTRvxsBHPPqonByitPILkqGNJ3jbJOmdMP639X98cPJnXq3z9gFXJIL725sj06O+zY1Kd4gz1YX7s+naONtJ09tPaSN9lI7lcZhrMMH7PGn4YQApZnJ0ZG/pNsgmJ6SvML5n6ZKpJy1LDBUUv/nk8nHYe7Szo7o69GMhAlnk5tOHJxh1vzMwbCODgOP49mGy5ROkFfiHj0OXvo/DiwarjlF6UG3ORGJ2FoL9tOpzmJkY6/mji+R7elH8kJargl3lRbS7ztFHtOhKS6FpcgwqPEfWL9ef575gkHT6XregsCZ6dTgehZq+2c1vWUovF+rO5TGw0NP1bNdahmt3WdGP94Q51Xl41ZZkxYep95akLgVWVEDJddkPOYHamgjUrzjjikt4fK1hcr2iOdvqV6tWQ61WZSy006J6KnmdRwUj5jDHfSMXr8fcmZfSE2ryHjKTVHXZCgtmB/mspo9HaeN3fI2f6KIDJr24aKhs0F/0nlEzlmWp4yQXp/29j/T+GpcusXozC4OumY3jkE1dyTHgDvNsExgNh8gYzRoVlGl5T5zcGD6L5r9k9BjTXSveV+pmQ58Ci7zHF2zE69K4kNAHfnXc51a9ds8tuwPxMbuExG3mq4EfOa44NSnoHSA7WiVmYrOeIOF5+MeUwdHIgzDdjyHkvUKeT9UdnKBPrBCYMdVUsvCsMntE1s4z6ZvfEhqby9Ykq+Cvrocm6QA/XjebCu1nrQlnw+1uIf+Ayi+pV1EZHPPIgaXVUpF4phY9ihwYzJv13wUgYfT7HSCr9TkpxHLMy2/0D1Y+uzfPcYtwX5nvHvv1vGrUszFdgBtSZJAyilQiuMp/AmL/BE6pziU7eesZdyEUV3vQ/R0tF39v0iTq6A3JSaII9anAVGsTFtUNoe/fEfgAxdzw3iCnOCTtvdK4nAQQc2bk8l80eeI8dsbMTUjFIGvJZi5isP5hVtjN0HGKlBjH5rjDXHG6zWFUXHIt1AZzR6EEN8LTK7/37kXZGsbf0Z+rQFRlveMX1atO65bWYuXOianA+t00iMXx3sc0n4FGo/SV2YUvUYhB1cqG+xXQ5/LvufcFceK7OJXPyqBkqxknVuT5Rq1dXJlEHaKM+499xaDhJgLdudItjnuPhoAUbzo4qtbOP5JhzOu8lz6shd2DIkYvYnR9FTo0BXsfV40i2yvdnLMkYUpdxlMkR4rD6/MrA1utQYm1bd/ZL7hSVGj2sC3bBfw9qIgqcesr3aOuVlah5qgK786PcuuXTk0mf5xPBF8SSRnvNJEqGcpOcLuPpK1ZwSHv5Tv3y3yRV27lMlzQAu7AlKjeIUWhxL/pxJiXlCf/WmUZpnfkxCb0bE9o1Najbjzc/XMLW+c8dJOaZNBwOKpevMkAy0V1cZTVUM88UjQbcvJ9bSnNSxrNHhnEQfcGsI3Mjx547KsNSmiUW6o8KHm06flkOL0JSVkvDaP3lepgerNBEHq5HNc9pdDaCBrFcr3hZRmgTHeCLeGTO//P65zwZUWje8E+SDePvf8ryGVTq5/m8sf/USO0k53+NeR/CwUXlTqknlRrEKDT8cvYLgxdkCc9htg7OLsXI4LFv7pxdaG80BbwQWDL/PjUYZeG+59erwlAtzzNxZglImXSO7ukI5qOquyoqFI1Uv2E0IjMZZLiz08kNRGUamoOnm8oqMha9+OcixnDTO0p2ZDSHfTWbw3YRlpeQmcS5TRYnYVqafLk4p7sEjajna9iyOS9V1+v8zCOtnTHcfj7YTWUUr0OEpXqVjGzPxrz9mqyStxuh2m/rUK7K4OEaxIPztOPt3GgGJ7AqKl12EckhDs53OOy8X8xiky17CHXSv2Q4Zj5HqZPrTw6Gs2Oo4mVZLxTg5DOnD+Ivn/VFWUhGlmVZF8Zh1vE9rcsGURRMrvXCdWnyHHY5i0tM0OW7y9bB/pMcOd1tXCqQ8t4R7i/j8tiUuSSEaWbPfZlgMeChtzF7Ec7Xlq0E51Jl2ih8abnHA2iE8GV2ygztw83huPIKIEP6s3qYleUmY0jTpT2oAYXTm0TbT1/rBwuZn3IRACaWweCzTddyhgW70Y93bdmmTyXreJZI1YyDlqqozCC6nI7YdT/NU7O5xe9mNmfG0qlbk5NKL+0Ymzw4eS0IZsZmObQ7yvKQ7b7zrOfJCad4ir+PGkR33bG2c0wkFzCQIyApB5HzEHfYR2k5AJ3cFYFei9GP0joB907Xd4IIZmaoOIQdUpnL6UhSFeQgGhGM4p/THLjlxAar0HoeKr6wFnUJlxYw5TxuPVf8rnqb2tBC3mDAATQMliLT/5ZermVhFhaGLyb4WCbZ2WutcwX+RQZt1tYiKX8tC44Worch3iYG0yOvF21G9bhEMZNe/MW/WzCInHWGAxnyfy12nEup0uwCyqRhsYvjk3k1XQLwbtrN8zHU6B80iiiT/tX8MF4vtCgnqkReQQ6hXpWAmQuHA32S952aX7UgSwttj9blKHSefSLfoYG3VRlpLrcFZwWDV8tlHpdTsoPKWk6RjDl7N+ScPOj0Z+dPvFPGLC01SS7w5V0O1hlvF7iRNAbBDeQkudg+yge6ov7E7ymdBamLRtjL5+L7dVJ0+nqs5Xe1rn3Y89mlRtiNDhz77npxkAzmwmxqywZG8Tehmz7pJv8Y1YVXUb7fWkiO3Lj+r1Mf3PKBYk6qSfRL6+rww/a2Ie9zw1hW6Fa3nftjrvNo8kxlFMepbvA5/uLfLc4hkrFA9Bk02g7aVoh5BxRPosJwVQMMdec5yRI9mC+A5U7r6aqzc/AFRhn7z06mlzZxK9pIpR6V+FHyuI8rMmNEUWrP421poiz1XPk48e0YeP2boXtJhEJz/sWq51k4k888dx8n7p1+5/Vz7p7cnKeTzXD2fBo6l1Ah7KycsUeO0e4byTK30BzZQjlBrqUM3InPc9yaczp3j355a+dU7BydfQ+MzT4Ub+nDo2gqm5/JzbTSe7Re7GV8Ls5qT+d6QaPFT3SNO/r9UZSWqze/ONXFm/zGgJ0X6hhfx3lKl+vP4nuy9am77lx2Xe7J/Tg2UPNzxcYGu/Hz09/epuVGXfuuFsozOcp2qQbBkwkcjElujee+j6L6Csntn/LAdcdEUiiXHzuF1DEm68KsrnF7XKw3ky6vB+TtpaBEbAKD/v37uExtwodoTevDejXr47ycVN7Oi+YhjxN5mG0dnr0/e27O+5wYWRmovF8qE5ecZb4OPdpN6Dx+xlnbsXUebZpFFaVK5LZPZf8stH1xvgxTvpQumS5GjtmkyyuXVrjUX5hsRL21Ep0xb+deetTInQedrChvOfk3rMHuJWez3c/TsrnzkBSxeC6+r5NNKd7KgMsgNOGDzPLunsg5Sc4h0TErkptXAmegWUxBNutY+WtWTi5p80MHJA0u1wfn5vI382ArThvHCbSjzm4UHWjfl4HwcfJcyRydLoqU8/GqV8qte+3N/8TrEPN32vNrHVxGOxjMZC6I3tXsPfE1Mhgc+85hYT7lyLUZ/mJwzM+vYfOcy8p3tVyeiXsr0pk+GJUWhI3Phns/gmg3lAfbyvF6wAxRvz5wFORym0ypV2/JCb8AT/iza8vAlcNCv/Rxwya/TcSWz1I7jZ9xue3QSPsSEjEm9E+vkzHtWhl03LYbxUDQ9gF4eI9mOoAoRJkXMn9MaYD8csnznl1fpWRN4A6JRp8DXoRN8tnPnNXEsIcB5gvLHFkmmaPKNIzWND1biBrkqD1tRtQY3qcZHZZfY/luU+dCL/LKGFcWdN6VHk68js7uSADmPH4291feSdp8c893OFp3HQ3hqBM5AyHoMaTC+SOn9tj4va0iLm96J+cLQRs8ItKcqWP9rucI5OiY+PkellGed8lBWAjBBUef8TxNBxbnT7ixUdkNOTCJPMuf7TvldUw0gZ+3fmyCO2W67yHd9690jb6JRy+27sCnuLPalHUoeuePuR5yhKG9b3uiuGw7rKrwhqx38/d6cLv+vlx52nKKywCszFzq87trHV5Red8m520DM38nybo4NZPx5vPN6wI9/yO+d75vLlNOQfZh2BnFx7KiYnevj3P5JlIn0v0MOsbJ0UXbQFSvH7bJXPUqeY30dcPr3p/Fo1/i9IFp18vSL0VH3mZ9hqQdWdXmuZ1Sfef1xr8azdHT69s8J9tItnkykrPzbepbkmyqE8m6Oj9/Q8m627wgNA0yiCb+nhM/gyAIraDSZReCIAiC0BbEIAqCIAgCiEEUBEEQBIsYREEQBEEAMYiCIAiCYKll+ycfDBxehPPMBZWvvROEsghN5zEIgtA6/g/ubKRF85fOFwAAAABJRU5ErkJggg==");
}

/* ==========================================================================
   2. BASE
   ========================================================================== */
* { box-sizing: border-box; }

html { background: #E7E7ED; }

body {
  margin: 0;
  font-family: var(--sans);
  font-size: 9.5pt;
  font-weight: 400;
  line-height: 1.58;
  color: var(--ink);
  -webkit-font-smoothing: antialiased;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

/* Toda cifra en Sans va con numerales tabulares para que las columnas
   de dato queden alineadas. En Mono ya lo están por definición. */
.num { font-variant-numeric: tabular-nums; }

/* ==========================================================================
   3. HOJA + MARCA DE AGUA
   ========================================================================== */
.sheet {
  position: relative;
  width: 210mm;
  height: 297mm;
  padding: var(--pad-top) var(--pad-x) var(--pad-bottom);
  background: var(--paper);
  overflow: hidden;
}

/* Marca de agua: el logotipo del brandbook, grande y al 2%. A esa
   opacidad no compite con el texto ni ensucia la fotocopia, y evita el
   sello rotado tipo "BORRADOR", que en un informe al cliente resta. */
.sheet::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 0;
  background-image: var(--wm);
  background-repeat: no-repeat;
  background-position: center 60%;
  background-size: 130mm auto;
  opacity: 0.02;
  pointer-events: none;
}

/* El contenido va por encima de la marca de agua. */
.sheet > * { position: relative; z-index: 1; }

/* ==========================================================================
   4. MEMBRETE
   ========================================================================== */
.masthead {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12mm;
  padding-bottom: 4.5mm;
  border-bottom: 0.75pt solid var(--violet);
}

.masthead__logo { width: 42mm; height: auto; display: block; }
.masthead__logo path { fill: var(--violet); }

/* Ficha del documento. La frase va en Sans, en caja baja y sin tracking
   abierto; solo la referencia —número y fecha— va en Mono, porque es dato. */
.masthead__doc { text-align: right; padding-top: 1.8mm; }

.masthead__doc-kind {
  font-size: 8pt;
  font-weight: 500;
  color: var(--ink-2);
}

.masthead__doc-ref {
  margin-top: 1mm;
  font-family: var(--mono);
  font-size: 7pt;
  color: var(--ink-3);
}

/* Encabezado corriente, páginas 2 y 3. Repone el contexto que el lector
   pierde al dar vuelta la hoja: qué conjunto es y qué forma tenía. */
.runhead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8mm;
  padding-bottom: 3.5mm;
  border-bottom: 0.5pt solid var(--rule);
}

.runhead__left { display: flex; align-items: center; gap: 3.2mm; }
.runhead__iso  { width: 4.6mm; height: 4.6mm; display: block; flex: none; }
.runhead__iso path { fill: var(--violet); }

.runhead__name {
  font-size: 7.5pt;
  color: var(--ink-3);
}

.runhead__count {
  font-family: var(--mono);
  font-size: 7pt;
  color: var(--ink-3);
}

.runhead__band { display: flex; width: 40mm; height: 2mm; gap: 0.4mm; flex: none; }

/* ==========================================================================
   5. TITULAR
   ========================================================================== */
.title { margin-top: 12mm; }

.title__name {
  margin: 0;
  max-width: 152mm;
  font-size: 25pt;
  font-weight: 600;
  line-height: 1.14;
  letter-spacing: -0.022em;
}

.title__scope {
  margin: 4mm 0 0;
  font-size: 9.5pt;
  color: var(--ink-2);
}

.title__scope strong { color: var(--ink); font-weight: 500; }

/* ==========================================================================
   6. FICHA DE ALCANCE — qué se midió. Sostiene la credibilidad del informe.
      Sin filetes ni celdas: rótulo en Mono, valor en Sans.
   ========================================================================== */
.facts {
  margin-top: 9mm;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 9mm;
}

.facts__key {
  font-family: var(--mono);
  font-size: 7pt;
  color: var(--ink-3);
}

.facts__val {
  margin-top: 1.2mm;
  font-size: 9pt;
  line-height: 1.45;
  color: var(--ink);
}

/* Lista de agentes cuando no entra en la columna de la grilla (5 o más):
   pasa a una línea propia a todo el ancho, separada por "|" para que no
   se confunda con una coma dentro de un nombre compuesto. */
.facts__wide { margin-top: 3mm; }

/* ==========================================================================
   7. SECCIONES
      La marca violeta sobre el título reemplaza al rótulo al margen:
      ordena sin construir una cuadrícula de tabla.
   ========================================================================== */
.sec { margin-top: 10mm; }

.sec__mark {
  width: 7mm;
  height: 1.5pt;
  background: var(--violet);
  margin-bottom: 3mm;
}

.sec__head {
  margin: 0 0 4mm;
  font-size: 13pt;
  font-weight: 600;
  letter-spacing: -0.012em;
  break-after: avoid;
}

.prose { margin: 0; max-width: var(--measure); }
.prose--lead { font-size: 10.5pt; line-height: 1.62; }

/* ==========================================================================
   8. SATISFACCIÓN
      Una línea de estadística, no tres tarjetas: la cifra en Mono lleva
      su palabra en Sans al lado, y nada las encierra.
   ========================================================================== */
.stats { display: flex; align-items: baseline; gap: 13mm; }

.stat { display: flex; align-items: baseline; gap: 2mm; white-space: nowrap; }

/* Cifra de display: Sans 600 con numerales proporcionales. En Mono la coma
   decimal ocupa una celda completa y "68,4%" se lee como "68 , 4%". */
.stat__num {
  font-size: 23pt;
  font-weight: 600;
  letter-spacing: -0.03em;
  line-height: 1;
}

.stat__num--positiva { color: var(--sat-positiva); }
.stat__num--neutra   { color: var(--ink); }
.stat__num--negativa { color: var(--sat-negativa); }

.stat__word { font-size: 9pt; color: var(--ink-2); }

/* BANDA DE SATISFACCIÓN — el elemento distintivo del informe.
   Una sola barra proporcional: la forma del conjunto en una línea.
   Reemplaza al gráfico de torta, que en papel es menos preciso. */
.band { margin-top: 6.5mm; break-inside: avoid; }

.band__bar { display: flex; height: 12mm; gap: 0.6mm; }

.band__seg { min-width: 0.4mm; }
.band__seg--positiva { background: var(--sat-positiva); }
.band__seg--neutra   { background: var(--sat-neutra); }
.band__seg--negativa { background: var(--sat-negativa); }
.band__seg--sindato  { background: transparent; border: 0.5pt solid var(--ink-3); }

.band__caption {
  margin-top: 3mm;
  font-family: var(--mono);
  font-size: 7pt;
  line-height: 1.5;
  color: var(--ink-3);
}

.band__caption strong { color: var(--ink-2); font-weight: 500; }

/* ==========================================================================
   9. LISTAS — sin filetes: separación por aire y una marca violeta colgada.
   ========================================================================== */
.list { margin: 0; padding: 0; list-style: none; }

.list__item {
  position: relative;
  margin-top: 3.4mm;
  padding-left: 5.5mm;
  max-width: var(--measure);
  break-inside: avoid;
}

.list__item:first-child { margin-top: 0; }

.list__item::before {
  content: "";
  position: absolute;
  left: 0;
  top: 1.9mm;
  width: 1.7mm;
  height: 1.7mm;
  background: var(--violet);
}

/* Recomendaciones numeradas. La numeración cumple una función del papel:
   en una reunión con el informe impreso alguien dice "la recomendación 4"
   y todos la encuentran. En pantalla no hace falta, ahí hay Ctrl+F.
   El número va en Mono porque es una referencia, no prosa. */
.reclist { margin: 0; padding: 0; list-style: none; }

.rec {
  display: grid;
  grid-template-columns: 6.5mm 1fr;
  column-gap: 2mm;
  margin-top: 3.6mm;
  break-inside: avoid;
}

.rec:first-child { margin-top: 0; }

.rec__num {
  font-family: var(--mono);
  font-size: 8.5pt;
  font-weight: 500;
  color: var(--violet);
  padding-top: 0.5mm;
}

.rec__text { max-width: var(--measure); }

/* ==========================================================================
   10. EMOCIONES
       El ancho de la barra es relativo al primer puesto, no al total.
       Violeta claro para no competir con la codificación de satisfacción,
       que es la que carga significado semántico.
   ========================================================================== */
.emotions { margin: 0; padding: 0; list-style: none; }

.emotion {
  display: grid;
  grid-template-columns: 33mm 1fr 21mm;
  align-items: center;
  column-gap: 5mm;
  margin-top: 2.8mm;
  break-inside: avoid;
}

.emotion:first-child { margin-top: 0; }

.emotion__label { font-size: 8.5pt; }
.emotion__track { height: 2.6mm; background: var(--track); }
.emotion__fill  { height: 100%; background: var(--violet-light); }

.emotion__val {
  font-family: var(--mono);
  font-size: 7.5pt;
  text-align: right;
  color: var(--ink-3);
}

/* ==========================================================================
   11. NOTA METODOLÓGICA
   ========================================================================== */
.note { margin-top: 4.5mm; max-width: var(--measure); break-inside: avoid; }
.note:first-child { margin-top: 0; }

.note__key {
  font-size: 8.5pt;
  font-weight: 500;
  color: var(--violet);
}

.note__text {
  margin: 1.4mm 0 0;
  font-size: 8pt;
  line-height: 1.55;
  color: var(--ink-2);
}

/* ==========================================================================
   12. PIE — logotipo con slogan del brandbook, no el slogan tipeado.
   ========================================================================== */
.foot {
  position: absolute;
  left: var(--pad-x);
  right: var(--pad-x);
  bottom: 13mm;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8mm;
  padding-top: 3.5mm;
  border-top: 0.5pt solid var(--rule);
}

/* 452 x 165 px del brandbook → 19.2 x 7 mm conserva la proporción exacta. */
.foot__logo {
  width: 19.2mm;
  height: 7mm;
  flex: none;
  background-image: var(--wm);
  background-size: contain;
  background-repeat: no-repeat;
  background-position: left center;
}

.foot__conf {
  flex: 1;
  text-align: center;
  font-size: 6.8pt;
  color: var(--ink-3);
}

.foot__page {
  font-family: var(--mono);
  font-size: 7.5pt;
  color: var(--ink-2);
  white-space: nowrap;
}

/* ==========================================================================
   13. PANTALLA (solo previsualización) / IMPRESIÓN
   ========================================================================== */
@media screen {
  body { padding: 12mm 0; }
  .sheet { margin: 0 auto 10mm; box-shadow: 0 2px 20px rgba(40, 41, 56, 0.15); }
}

@page { size: A4; margin: 0; }

@media print {
  html { background: none; }
  body { padding: 0; }
  .sheet { margin: 0; box-shadow: none; break-after: page; }
  .sheet:last-child { break-after: auto; }
}`;

const MASTHEAD_LOGO_SVG = `<svg class="masthead__logo" aria-label="EVOLTIS" viewBox="0 0 621 145" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M231.015 25.0383H198.759C180.966 25.0383 169.937 36.3544 169.937 54.5254V91.9554C169.937 110.235 180.966 121.442 198.759 121.442H231.015C231.847 121.442 232.472 120.681 232.472 119.919V108.821C232.472 108.059 231.743 107.297 231.015 107.297H198.759C189.186 107.297 184.088 101.966 184.088 91.8466V79.8777H225.292C226.125 79.8777 226.749 79.116 226.749 78.3544V67.6912C226.749 66.9295 226.02 66.1678 225.292 66.1678H184.088V54.4166C184.088 44.2974 189.082 38.9658 198.759 38.9658H231.015C231.847 38.9658 232.472 38.2041 232.472 37.4425V26.4528C232.472 25.5824 231.743 24.9295 231.015 24.9295V25.0383Z" fill="#5351FB"/>
<path d="M357.852 24.8136C333.291 24.8136 313.26 46.505 313.26 73.0401C313.26 99.5752 332.788 121.372 357.852 121.372C382.916 121.372 402.343 99.6805 402.343 73.0401C402.343 46.3997 383.218 24.8136 357.852 24.8136ZM357.852 107.788C340.337 107.788 326.648 92.4149 326.648 73.0401C326.648 53.6653 340.639 38.2918 357.852 38.2918C375.065 38.2918 389.056 53.2441 389.056 73.0401C389.056 92.8361 375.065 107.788 357.852 107.788Z" fill="#5351FB"/>
<path d="M461.49 107.406H437.038C427.986 107.406 423.407 102.292 423.407 91.9551V26.5614C423.407 25.7997 422.783 25.0381 422.055 25.0381H411.65C410.921 25.0381 410.297 25.7997 410.297 26.5614V91.9551C410.297 110.235 420.598 121.442 437.142 121.442H461.594C462.323 121.442 462.947 120.681 462.947 119.919V108.82C462.947 107.95 462.427 107.297 461.594 107.297L461.49 107.406Z" fill="#5351FB"/>
<path d="M520.694 25.0381H454.413C453.685 25.0381 452.956 25.7997 452.956 26.5614V37.4422C452.956 38.2038 453.581 38.9655 454.413 38.9655H480.634V119.919C480.634 120.681 481.258 121.442 482.091 121.442H487.501C490.415 121.442 494.265 118.178 494.369 107.732V38.9655H520.59C521.318 38.9655 522.047 38.2038 522.047 37.4422V26.5614C522.047 25.6909 521.422 25.0381 520.59 25.0381H520.694Z" fill="#5351FB"/>
<path d="M543.482 25.0381H530.476C529.539 25.0381 528.707 25.6909 528.707 26.5614V119.919C528.707 120.681 529.539 121.442 530.476 121.442H536.927C540.361 121.442 544.939 118.178 545.251 107.841V26.6702C545.251 25.9085 544.523 25.1469 543.482 25.1469V25.0381Z" fill="#5351FB"/>
<path d="M619.336 25.0381H617.879C606.329 25.0381 597.693 28.3023 591.034 35.3748C583.126 43.6443 579.692 54.9603 579.692 73.0225C579.692 91.0846 576.362 97.1779 570.952 102.074C566.998 105.556 561.379 107.188 553.263 107.188C545.147 107.188 551.806 107.95 551.806 108.712V119.919C551.806 120.681 552.43 121.442 553.263 121.442H554.303C565.021 121.442 573.657 118.613 579.9 113.064C589.681 104.359 593.531 93.2608 593.531 73.1313C593.531 53.0018 596.861 49.1935 602.271 44.2971C605.705 41.0329 611.22 39.4007 619.544 39.2919C620.272 39.2919 621.001 38.5303 621.001 37.7686V26.6702C621.001 25.9085 620.376 25.1469 619.44 25.1469L619.336 25.0381Z" fill="#5351FB"/>
<path d="M314.465 25.6909C314.153 25.2557 313.84 25.0381 313.32 25.0381H301.77C301.25 25.0381 300.626 25.4733 300.418 26.0173L276.382 96.6339C276.382 96.6339 275.966 97.5043 275.237 97.3955C274.301 97.3955 273.989 96.6339 273.989 96.6339L250.057 26.0173C249.849 25.3645 249.328 25.0381 248.704 25.0381H237.05C236.53 25.0381 236.114 25.2557 235.906 25.6909C235.593 26.1261 235.593 26.5614 235.697 27.1054L265.352 114.37C265.56 115.022 267.641 121.225 275.133 121.442C283.041 120.898 285.018 114.805 285.226 114.261L314.569 26.9966C314.569 26.5614 314.569 26.1261 314.257 25.6909H314.465Z" fill="#5351FB"/>
<path d="M44.3395 109.742C34.3888 98.625 28.9102 84.2221 28.9102 69.1894C28.9102 63.3382 29.7201 57.6223 31.3273 52.1117C24.963 60.1632 20.8418 69.8748 19.5111 80.1864C19.1813 82.7272 19.0156 85.2324 19.0156 87.6322C19.0156 101.771 24.17 115.318 33.5283 125.775C44.1037 137.593 59.1849 144.37 74.9019 144.37C93.4965 144.37 110.829 134.838 121.134 119.493C111.464 126.003 100.094 129.515 88.3281 129.515C85.8127 129.515 83.1907 129.334 80.5363 128.981C66.6426 127.131 53.7863 120.298 44.3395 109.742Z" fill="#5351FB"/>
<path d="M101.094 34.6266C111.182 45.7445 116.736 60.1458 116.736 75.1782C116.736 81.0308 115.915 86.7479 114.287 92.2585C120.738 84.2071 124.916 74.4928 126.266 64.1814C126.599 61.6406 126.767 59.1383 126.767 56.7371C126.767 42.5981 121.543 29.0519 112.054 18.5951C101.333 6.77749 86.0451 0.00012207 70.1113 0.00012207C51.2602 0.00012207 33.6871 9.53205 23.2405 24.8766C33.0438 18.3671 44.5719 14.8559 56.4985 14.8559C59.0486 14.8559 61.7068 15.0355 64.3978 15.3874C78.4845 17.24 91.5167 24.0715 101.094 34.6266Z" fill="#5351FB"/>
<path d="M15.389 80.5392C17.2386 66.6457 24.0717 53.7896 34.6256 44.3429C45.7437 34.3937 60.1451 28.9138 75.1792 28.9138C81.0319 28.9138 86.7477 29.7251 92.2583 31.3309C84.2069 24.9681 74.4953 20.8469 64.1823 19.5149C61.6414 19.1864 59.1362 19.0194 56.7379 19.0194C42.5986 19.0194 29.0522 24.1736 18.5967 33.5319C6.77888 44.1071 0 59.1881 0 74.9063C0 93.4992 9.5335 110.835 24.8769 121.137C18.3687 111.468 14.856 100.096 14.856 88.3324C14.856 85.817 15.0355 83.1935 15.389 80.5392Z" fill="#5351FB"/>
<path d="M119.494 23.2397C126.002 33.043 129.514 44.5711 129.514 56.4991C129.514 59.0506 129.335 61.7089 128.981 64.397C127.132 78.4823 120.3 91.516 109.745 101.093C98.6253 111.182 84.2253 116.735 69.1926 116.735C63.34 116.735 57.6227 115.913 52.1121 114.283C60.1635 120.737 69.878 124.915 80.1896 126.265C82.7304 126.598 85.2356 126.766 87.634 126.766C101.772 126.766 115.318 121.541 125.774 112.055C137.591 101.332 144.37 86.0458 144.37 70.1105C144.37 51.258 134.838 33.6848 119.494 23.2397Z" fill="#5351FB"/>
<path d="M104.932 72.533C104.932 90.4295 90.4236 104.933 72.5327 104.933C54.6474 104.933 40.1394 90.4295 40.1394 72.533C40.1394 54.6477 54.6474 40.1411 72.5327 40.1411C90.4236 40.1411 104.932 54.6477 104.932 72.533Z" fill="#5351FB"/>
</svg>`;

const RUNHEAD_LOGO_SVG = `<svg class="runhead__iso" aria-label="EVOLTIS" viewBox="0 0 151 151" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M46.2586 114.489C35.8774 102.892 30.1618 87.8657 30.1618 72.1827C30.1618 66.0783 31.0067 60.1152 32.6835 54.3662C26.0438 62.766 21.7443 72.8977 20.356 83.6554C20.0119 86.3062 19.8391 88.9198 19.8391 91.4234C19.8391 106.174 25.2165 120.307 34.9797 131.216C46.0126 143.545 61.7462 150.616 78.1432 150.616C97.5422 150.616 115.625 140.671 126.375 124.663C116.287 131.454 104.425 135.119 92.1503 135.119C89.5261 135.119 86.7905 134.93 84.0213 134.561C69.5266 132.631 56.1141 125.503 46.2586 114.489Z" fill="#5351FB"/>
<path d="M105.469 36.1245C115.993 47.7234 121.788 62.7477 121.788 78.4305C121.788 84.5362 120.931 90.5007 119.233 96.2497C125.962 87.85 130.321 77.7155 131.73 66.9579C132.077 64.3072 132.253 61.6966 132.253 59.1916C132.253 44.4409 126.803 30.3086 116.903 19.3995C105.718 7.07057 89.7691 0 73.1459 0C53.4793 0 35.1458 9.94431 24.2473 25.9527C34.4748 19.1616 46.5016 15.4985 58.9441 15.4985C61.6046 15.4985 64.3778 15.6858 67.1852 16.053C81.8813 17.9857 95.4774 25.1127 105.469 36.1245Z" fill="#5351FB"/>
<path d="M16.0547 84.0235C17.9844 69.5289 25.1131 56.1166 36.1236 46.2612C47.7227 35.8816 62.7471 30.1646 78.4317 30.1646C84.5375 30.1646 90.5006 31.011 96.2497 32.6863C87.8499 26.0482 77.7181 21.7488 66.9589 20.3591C64.3082 20.0164 61.6946 19.8421 59.1925 19.8421C44.4416 19.8421 30.3091 25.2194 19.4013 34.9825C7.07216 46.0152 0 61.7486 0 78.1469C0 97.5442 9.94594 115.629 25.9531 126.378C19.1634 116.29 15.4987 104.427 15.4987 92.1538C15.4987 89.5296 15.686 86.7926 16.0547 84.0235Z" fill="#5351FB"/>
<path d="M124.664 24.245C131.454 34.4725 135.119 46.4993 135.119 58.9433C135.119 61.6052 134.931 64.3785 134.563 67.1829C132.633 81.8776 125.506 95.4751 114.494 105.467C102.893 115.992 87.8702 121.785 72.1871 121.785C66.0813 121.785 60.1167 120.927 54.3677 119.227C62.7675 125.96 72.9022 130.319 83.6599 131.728C86.3106 132.075 88.9242 132.25 91.4263 132.25C106.176 132.25 120.308 126.799 131.216 116.902C143.545 105.716 150.617 89.7683 150.617 73.1436C150.617 53.4755 140.673 35.1421 124.664 24.245Z" fill="#5351FB"/>
<path d="M109.474 75.6707C109.474 94.3415 94.338 109.473 75.6731 109.473C57.014 109.473 41.8784 94.3415 41.8784 75.6707C41.8784 57.0116 57.014 41.8775 75.6731 41.8775C94.338 41.8775 109.474 57.0116 109.474 75.6707Z" fill="#5351FB"/>
</svg>`;

/* ==========================================================================
   ESCAPADO — todos los textos del resumen son salida de un LLM sobre
   transcripciones de clientes: no confiar, escapar siempre.
   ========================================================================== */
function escapeHtml(value) {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function sanitizeFilenamePart(value) {
  return String(value).replace(/[\\/:*?"<>|]/g, "-");
}

/* ==========================================================================
   FORMATO — locale es-AR
   ========================================================================== */
const MONTHS_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function pad2(n) {
  return String(n).padStart(2, "0");
}

function parseDateOnly(value) {
  if (!value) return null;
  const datePart = String(value).split(" ")[0].split("T")[0];
  const [y, m, d] = datePart.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function formatDateLong(d) {
  return `${d.getDate()} de ${MONTHS_ES[d.getMonth()]} de ${d.getFullYear()}`;
}

function formatDateShort(d) {
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function formatDateRangeLong(from, to, t) {
  const dFrom = parseDateOnly(from);
  const dTo = parseDateOnly(to);
  if (!dFrom && !dTo) {
    return t("insights.report.noPeriod", { defaultValue: "Período no especificado" });
  }
  if (dFrom && dTo) {
    const sameMonth = dFrom.getMonth() === dTo.getMonth() && dFrom.getFullYear() === dTo.getFullYear();
    if (sameMonth) {
      return `${dFrom.getDate()} al ${dTo.getDate()} de ${MONTHS_ES[dTo.getMonth()]} de ${dTo.getFullYear()}`;
    }
    return `${formatDateLong(dFrom)} al ${formatDateLong(dTo)}`;
  }
  return formatDateLong(dFrom || dTo);
}

function formatDateRangeShort(from, to, t) {
  const dFrom = parseDateOnly(from);
  const dTo = parseDateOnly(to);
  if (!dFrom && !dTo) {
    return t("insights.report.noPeriod", { defaultValue: "Período no especificado" });
  }
  if (dFrom && dTo) return `${formatDateShort(dFrom)} \u2013 ${formatDateShort(dTo)}`;
  return formatDateShort(dFrom || dTo);
}

function formatGeneratedAt(value) {
  if (!value) return "\u2014";
  const d = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return String(value);
  return `${d.getDate()} de ${MONTHS_ES[d.getMonth()]} de ${d.getFullYear()}, ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function formatPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "\u2014";
  return `${n.toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

// A partir de esta cantidad de agentes, la lista sale de la columna de la
// ficha de alcance y pasa a una línea propia a todo el ancho (ver armado
// de la página 1 en buildSummaryReportHtml).
const AGENTS_INLINE_MAX = 4;

// Aun a todo el ancho, listar cientos de nombres desborda la página 1 (no
// hay una segunda hoja con membrete a la que seguir). Calibrado contra el
// render real en Chrome headless: a partir de ~30 nombres de ~24 caracteres
// separados por "|" el resto se resume como texto ("y N más").
const AGENTS_WIDE_DISPLAY_MAX = 30;

function toArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return [String(value)];
}

function formatListField(value, t) {
  const all = t("insights.all", { defaultValue: "Todos" });
  const list = toArray(value);
  if (list.length === 0) return all;
  return list.join(", ");
}

/* ==========================================================================
   BANDA DE SATISFACCIÓN
   ========================================================================== */
const SATISFACTION_KEYS = [
  ["Positiva", "positiva"],
  ["Neutra", "neutra"],
  ["Negativa", "negativa"],
  ["Sin dato", "sindato"],
];

function buildBandSegmentsHtml(counts) {
  return SATISFACTION_KEYS
    .filter(([key]) => (counts?.[key] || 0) > 0)
    .map(([key, cls]) => `<div class="band__seg band__seg--${cls}" style="flex: ${counts[key]}"></div>`)
    .join("");
}

function buildBandCaptionHtml(counts, callsAnalyzed, t) {
  const labelKeys = {
    Positiva: t("insights.report.band.positiva", { defaultValue: "positiva" }),
    Neutra: t("insights.report.band.neutra", { defaultValue: "neutra" }),
    Negativa: t("insights.report.band.negativa", { defaultValue: "negativa" }),
    "Sin dato": t("insights.report.band.sindato", { defaultValue: "sin dato" }),
  };
  const parts = [
    `${t("insights.report.band.over", { defaultValue: "Sobre" })} <strong class="num">${callsAnalyzed}</strong> ${t("insights.report.band.interactions", { defaultValue: "interacciones" })}`,
    ...SATISFACTION_KEYS.map(([key]) => `${escapeHtml(labelKeys[key])} <strong class="num">${counts?.[key] || 0}</strong>`),
  ];
  return parts.join(" &nbsp;\u00b7&nbsp; ");
}

/* ==========================================================================
   EMOCIONES — top 8, ancho relativo al primer puesto
   ========================================================================== */
/* Top 4 en vez de "todas": cada llamada puede traer más de una emoción a la
   vez (ver emotion_counts en cx_metrics.py), así que no hay un total del que
   repartir un %. Se muestra el conteo crudo contra el total de interacciones
   ("X de N interacciones") y la barra se mide contra ESE total (no contra el
   primer puesto): una barra llena significa "apareció en todas", nunca más. */
const EMOTIONS_TOP_N = 4;

function buildEmotionsSectionHtml(ranking, callsAnalyzed, t) {
  const top = (ranking || []).slice(0, EMOTIONS_TOP_N);
  if (top.length === 0 || callsAnalyzed <= 0) return "";
  const items = top.map(([label, count]) => {
    const width = Math.min(100, Math.round((count / callsAnalyzed) * 100));
    const cap = label ? label.charAt(0).toUpperCase() + label.slice(1) : "";
    const valueText = `${count} ${t("insights.report.emotions.of", { defaultValue: "de" })} ${callsAnalyzed} ${t("insights.report.interactions", { defaultValue: "interacciones" })}`;
    return `<li class="emotion">
        <div class="emotion__label">${escapeHtml(cap)}</div>
        <div class="emotion__track"><div class="emotion__fill" style="width: ${width}%"></div></div>
        <div class="emotion__val">${escapeHtml(valueText)}</div>
      </li>`;
  }).join("");
  return `<div class="sec" data-optional="true">
      <div class="sec__mark"></div>
      <h2 class="sec__head">${escapeHtml(t("insights.report.sections.emotions", { defaultValue: "Emociones predominantes" }))}</h2>
      <ul class="emotions">${items}</ul>
    </div>`;
}

/* ==========================================================================
   PAGINACIÓN — hoja de acciones (aspectos a mejorar + recomendaciones).
   Modelo en milímetros calibrado contra el render real en Chrome headless
   (arnés del Apéndice A de requerimiento.md) sobre el CSS verbatim de
   template.html, con margen de seguridad para no depender de un pixel
   exacto. Constantes:
     - RUNHEAD_MM: alto del encabezado corriente (páginas 2+).
     - SEC_HEADER_MM: alto de "marca violeta + título de sección" (§7).
     - ITEM_MARGIN_MM: separación entre ítems de una misma lista
       (.list__item / .rec no son idénticos en el CSS, pero la diferencia
       es submilimétrica; se usa el mayor de los dos por seguridad).
     - LINE_HEIGHT_MM / CHARS_PER_LINE: alto de línea y ancho de línea
       dentro de --measure (128mm) al tamaño de fuente del cuerpo (9.5pt).
   El presupuesto de página (PAGE_CONTENT_BUDGET_MM) deja ~8mm de margen
   respecto de la altura real disponible (273.2mm hasta el pie medidos)
   para absorber variación de fuente/renderizado entre entornos.
   ========================================================================== */
const RUNHEAD_MM = 24;
const SEC_HEADER_MM = 25;
const ITEM_MARGIN_MM = 3.6;
const LINE_HEIGHT_MM = 5.5;
const CHARS_PER_LINE = 76;
const PAGE_CONTENT_BUDGET_MM = 265;

const CONCLUSION_INLINE_MAX = 1100;
const CONCLUSION_PAGE_CHAR_BUDGET = 2500;

/* CONCLUSION_INLINE_MAX está calibrado (ver requerimiento.md §6) contra el
   nombre de ejemplo del template ("Cobranzas tramo temprano — Abril 2026",
   37 caracteres, 2 líneas de título). Un nombre más largo empuja el resto
   de la página 1 hacia abajo y ese margen de 1100 deja de ser válido: cada
   línea de título de más consume 10,05mm, que a razón de 6,0mm por línea /
   ~70 caracteres de la conclusión (mismo §6) equivale a ~117 caracteres
   menos de presupuesto. */
const TITLE_CHARS_PER_LINE = 32;
const TITLE_LINE_HEIGHT_MM = 10.05;
const TITLE_BASELINE_LINES = 2;
const CONCLUSION_MM_PER_CHAR = 6.0 / 70;

function estimateTitleLines(name) {
  return Math.max(1, Math.ceil((name || "").length / TITLE_CHARS_PER_LINE));
}

function effectiveConclusionInlineMax(name) {
  const extraLines = Math.max(0, estimateTitleLines(name) - TITLE_BASELINE_LINES);
  if (extraLines === 0) return CONCLUSION_INLINE_MAX;
  const lostMm = extraLines * TITLE_LINE_HEIGHT_MM;
  const lostChars = lostMm / CONCLUSION_MM_PER_CHAR;
  return Math.max(0, CONCLUSION_INLINE_MAX - lostChars);
}

function computeItemLines(text) {
  return Math.max(1, Math.ceil((text || "").length / CHARS_PER_LINE));
}

function paginateActionSequence(aspectsToImprove, recommendations) {
  const sequence = [
    ...(aspectsToImprove || []).map((text) => ({ type: "aspect", text })),
    ...(recommendations || []).map((text) => ({ type: "rec", text })),
  ];
  if (sequence.length === 0) return [];

  const itemsBudgetMm = PAGE_CONTENT_BUDGET_MM - RUNHEAD_MM;

  const pages = [];
  let current = [];
  let usedMm = 0;
  let lastTypeOnPage = null;

  sequence.forEach((item) => {
    const isNewGroupOnPage = item.type !== lastTypeOnPage;
    const lines = computeItemLines(item.text);
    const cost = (isNewGroupOnPage ? SEC_HEADER_MM : ITEM_MARGIN_MM) + lines * LINE_HEIGHT_MM;

    if (current.length > 0 && usedMm + cost > itemsBudgetMm) {
      pages.push(current);
      current = [];
      usedMm = 0;
      lastTypeOnPage = null;
      usedMm += SEC_HEADER_MM + lines * LINE_HEIGHT_MM;
      current.push(item);
      lastTypeOnPage = item.type;
      return;
    }

    usedMm += cost;
    current.push(item);
    lastTypeOnPage = item.type;
  });
  if (current.length > 0) pages.push(current);

  return pages;
}

function buildAspectsListHtml(items) {
  return `<ul class="list">${items.map((text) => `<li class="list__item">${escapeHtml(text)}</li>`).join("")}</ul>`;
}

function buildRecommendationsListHtml(items, startNumber) {
  return `<ol class="reclist">${items.map((text, i) => `
      <li class="rec">
        <div class="rec__num">${startNumber + i}</div>
        <div class="rec__text">${escapeHtml(text)}</div>
      </li>`).join("")}</ol>`;
}

function buildActionPageInnerHtml(pageItems, seenTypes, recCounterRef, t) {
  const groups = [];
  pageItems.forEach((item) => {
    const last = groups[groups.length - 1];
    if (last && last.type === item.type) {
      last.items.push(item.text);
    } else {
      groups.push({ type: item.type, items: [item.text] });
    }
  });

  return groups.map((group) => {
    const isContinuation = seenTypes.has(group.type);
    seenTypes.add(group.type);
    const continuedSuffix = isContinuation
      ? ` ${t("insights.report.continued", { defaultValue: "(continuación)" })}`
      : "";

    if (group.type === "aspect") {
      const title = t("insights.report.sections.aspectsToImprove", { defaultValue: "Dónde se pierde la experiencia" });
      return `<div class="sec">
        <div class="sec__mark"></div>
        <h2 class="sec__head">${escapeHtml(title)}${continuedSuffix}</h2>
        ${buildAspectsListHtml(group.items)}
      </div>`;
    }

    const title = t("insights.report.sections.recommendations", { defaultValue: "Qué conviene hacer" });
    const startNumber = recCounterRef.value + 1;
    recCounterRef.value += group.items.length;
    return `<div class="sec">
      <div class="sec__mark"></div>
      <h2 class="sec__head">${escapeHtml(title)}${continuedSuffix}</h2>
      ${buildRecommendationsListHtml(group.items, startNumber)}
    </div>`;
  }).join("");
}

function chunkConclusion(conclusion) {
  if (conclusion.length <= CONCLUSION_PAGE_CHAR_BUDGET) return [conclusion];
  const chunks = [];
  let rest = conclusion;
  while (rest.length > CONCLUSION_PAGE_CHAR_BUDGET) {
    let cut = rest.lastIndexOf(" ", CONCLUSION_PAGE_CHAR_BUDGET);
    if (cut <= 0) cut = CONCLUSION_PAGE_CHAR_BUDGET;
    chunks.push(rest.slice(0, cut).trim());
    rest = rest.slice(cut).trim();
  }
  if (rest) chunks.push(rest);
  return chunks;
}

/* ==========================================================================
   ARMADO DE HOJAS
   ========================================================================== */
function buildFootHtml(pageNumber, totalPages, t) {
  return `<footer class="foot">
    <div class="foot__logo" role="img" aria-label="EVOLTIS \u2014 Experiencias que transforman."></div>
    <div class="foot__conf">${escapeHtml(t("insights.report.footer.confidential", { defaultValue: "Documento confidencial \u00b7 circulación limitada a personal autorizado" }))}</div>
    <div class="foot__page">${pageNumber} / ${totalPages}</div>
  </footer>`;
}

function buildRunheadHtml(name, callsAnalyzed, hasSatisfaction, counts, t) {
  const bandHtml = hasSatisfaction
    ? `<div class="runhead__band">${buildBandSegmentsHtml(counts)}</div>`
    : "";
  return `<header class="runhead">
    <div class="runhead__left">
      ${RUNHEAD_LOGO_SVG}
      <div>
        <span class="runhead__name">${escapeHtml(name)}</span>
        <span class="runhead__count">&nbsp;\u00b7&nbsp; <span class="num">${callsAnalyzed}</span> ${escapeHtml(t("insights.report.interactions", { defaultValue: "interacciones" }))}</span>
      </div>
    </div>
    ${bandHtml}
  </header>`;
}

function wrapSheet(innerHtml, pageNumber, totalPages, t) {
  return `<section class="sheet">
  ${innerHtml}
  ${buildFootHtml(pageNumber, totalPages, t)}
</section>`;
}

/**
 * Construye el HTML A4 de 3 (o más) páginas del informe de experiencia del
 * cliente. Función pura: no toca el DOM ni lee estado global.
 *
 * @param {object} detail - respuesta de api.getSummaryHistoryDetail(id)
 * @param {function} t - función de traducción de react-i18next
 * @returns {string} documento HTML completo, listo para window.print()
 */
export function buildSummaryReportHtml(detail, t) {
  const translate = t || ((key, opts) => (opts && opts.defaultValue) || key);

  const name = (detail?.name || "").trim()
    || translate("insights.report.unnamed", { defaultValue: "Resumen sin nombre" });
  const callsAnalyzed = Number(detail?.callsAnalyzed) || 0;
  const generatedAt = formatGeneratedAt(detail?.generatedAt);
  const periodLong = formatDateRangeLong(detail?.dateRange?.from, detail?.dateRange?.to, translate);
  const periodShort = formatDateRangeShort(detail?.dateRange?.from, detail?.dateRange?.to, translate);
  const supervisor = formatListField(detail?.supervisor, translate);

  // Con pocos agentes entra cómodo en la columna de la ficha (1/3 del ancho).
  // Con muchos (ej. 50), esa columna angosta se vuelve un bloque de texto
  // envuelto que puede llegar a ocupar una hoja entera — se saca de la
  // grilla y se muestra en una línea propia a todo el ancho, separada por
  // "|" en vez de coma.
  const agentList = toArray(detail?.agent);
  const agentsOverflow = agentList.length > AGENTS_INLINE_MAX;
  // Con desborde el rótulo de la ficha pasa de "Agentes" a "Total de
  // agentes" y el valor es solo el número: "Agentes / 50 agentes" repetía
  // la palabra sin agregar nada.
  const agentsLabel = agentsOverflow
    ? translate("insights.report.facts.agentsTotal", { defaultValue: "Total de agentes" })
    : translate("insights.report.facts.agents", { defaultValue: "Agentes" });
  const agent = agentsOverflow
    ? String(agentList.length)
    : formatListField(detail?.agent, translate);
  // Aun a todo el ancho, una lista muy larga (cientos de agentes) puede
  // desbordar la página 1 igual. Se acotan los nombres listados y se suma
  // un remanente en texto ("y 120 más") en vez de intentar paginar una
  // grilla que solo existe en la página 1 (el membrete no se repite).
  const agentsWideNames = agentList.slice(0, AGENTS_WIDE_DISPLAY_MAX);
  const agentsWideRemaining = agentList.length - agentsWideNames.length;
  const agentsWideList = agentsOverflow
    ? agentsWideNames.map((a) => escapeHtml(a)).join(" &nbsp;|&nbsp; ")
      + (agentsWideRemaining > 0
        ? ` &nbsp;${escapeHtml(translate("insights.report.facts.agentsMorePrefix", { defaultValue: "y" }))} ${agentsWideRemaining} ${escapeHtml(translate("insights.report.facts.agentsMoreSuffix", { defaultValue: "más" }))}`
        : "")
    : "";

  const conclusionRaw = (detail?.conclusion || "").trim();
  const conclusion = conclusionRaw
    || translate("insights.report.noConclusion", { defaultValue: "Sin conclusión disponible para este resumen." });

  const counts = detail?.satisfactionMetrics?.counts;
  const percentages = detail?.satisfactionMetrics?.percentages;
  const hasSatisfaction = callsAnalyzed > 0 && !!counts;

  const aspectsToImprove = Array.isArray(detail?.aspectsToImprove) ? detail.aspectsToImprove.filter(Boolean) : [];
  const recommendations = Array.isArray(detail?.recommendations) ? detail.recommendations.filter(Boolean) : [];
  const emotionsRanking = Array.isArray(detail?.emotionMetrics?.ranking) ? detail.emotionMetrics.ranking : [];

  // ------------------------------------------------------------------
  // Página 1 — membrete, título, ficha de alcance, satisfacción y,
  // si entra (<=1100 caracteres), la conclusión.
  // ------------------------------------------------------------------
  const satisfactionSectionHtml = hasSatisfaction
    ? `<div class="sec">
        <div class="sec__mark"></div>
        <h2 class="sec__head">${escapeHtml(translate("insights.report.sections.satisfaction", { defaultValue: "Satisfacción del período" }))}</h2>
        <div class="stats">
          <div class="stat">
            <span class="stat__num stat__num--positiva">${formatPercent(percentages?.Positiva)}</span>
            <span class="stat__word">${escapeHtml(translate("insights.report.band.positiva", { defaultValue: "positiva" }))}</span>
          </div>
          <div class="stat">
            <span class="stat__num stat__num--neutra">${formatPercent(percentages?.Neutra)}</span>
            <span class="stat__word">${escapeHtml(translate("insights.report.band.neutra", { defaultValue: "neutra" }))}</span>
          </div>
          <div class="stat">
            <span class="stat__num stat__num--negativa">${formatPercent(percentages?.Negativa)}</span>
            <span class="stat__word">${escapeHtml(translate("insights.report.band.negativa", { defaultValue: "negativa" }))}</span>
          </div>
        </div>
        <div class="band">
          <div class="band__bar">${buildBandSegmentsHtml(counts)}</div>
          <div class="band__caption">${buildBandCaptionHtml(counts, callsAnalyzed, translate)}</div>
        </div>
      </div>`
    : `<div class="sec">
        <div class="sec__mark"></div>
        <h2 class="sec__head">${escapeHtml(translate("insights.report.sections.satisfaction", { defaultValue: "Satisfacción del período" }))}</h2>
        <p class="prose">${escapeHtml(translate("insights.report.noSatisfactionData", { defaultValue: "Sin datos de satisfacción para el período." }))}</p>
      </div>`;

  // Si la lista de agentes se fue a la línea ancha (ver AGENTS_INLINE_MAX),
  // ya compite por el espacio de la página 1 de forma muy variable (de un
  // agente extra a decenas): en vez de modelar esa altura, la conclusión
  // pasa directo a su propia hoja, sin importar la longitud.
  const conclusionFitsInline = !agentsOverflow && conclusion.length <= effectiveConclusionInlineMax(name);
  const inlineConclusionHtml = conclusionFitsInline
    ? `<div class="sec">
        <div class="sec__mark"></div>
        <h2 class="sec__head">${escapeHtml(translate("insights.report.sections.conclusion", { defaultValue: "Conclusión" }))}</h2>
        <p class="prose prose--lead">${escapeHtml(conclusion)}</p>
      </div>`
    : "";

  const page1Inner = `<header class="masthead">
    ${MASTHEAD_LOGO_SVG}
    <div class="masthead__doc">
      <div class="masthead__doc-kind">${escapeHtml(translate("insights.report.masthead.docKind", { defaultValue: "Informe de experiencia del cliente" }))}</div>
      <div class="masthead__doc-ref"><span class="num">${escapeHtml(generatedAt)}</span></div>
    </div>
  </header>

  <div class="title">
    <h1 class="title__name">${escapeHtml(name)}</h1>
    <p class="title__scope">
      <span>${escapeHtml(periodLong)}</span>
      &nbsp;\u00b7&nbsp;
      <strong><span class="num">${callsAnalyzed}</span> ${escapeHtml(translate("insights.report.interactionsAnalyzed", { defaultValue: "interacciones analizadas" }))}</strong>
    </p>
  </div>

  <div class="facts">
    <div>
      <div class="facts__key">${escapeHtml(translate("insights.report.facts.period", { defaultValue: "Período" }))}</div>
      <div class="facts__val num">${escapeHtml(periodShort)}</div>
    </div>
    <div>
      <div class="facts__key">${escapeHtml(translate("insights.report.facts.supervisors", { defaultValue: "Supervisores" }))}</div>
      <div class="facts__val">${escapeHtml(supervisor)}</div>
    </div>
    <div>
      <div class="facts__key">${escapeHtml(agentsLabel)}</div>
      <div class="facts__val${agentsOverflow ? " num" : ""}">${escapeHtml(agent)}</div>
    </div>
  </div>
  ${agentsOverflow ? `<div class="facts__wide">
    <div class="facts__key">${escapeHtml(translate("insights.report.facts.agents", { defaultValue: "Agentes" }))}</div>
    <div class="facts__val">${agentsWideList}</div>
  </div>` : ""}

  ${satisfactionSectionHtml}
  ${inlineConclusionHtml}`;

  // ------------------------------------------------------------------
  // Página(s) de conclusión propia, si no entró en la página 1.
  // ------------------------------------------------------------------
  const conclusionOverflowPages = conclusionFitsInline ? [] : chunkConclusion(conclusion).map((chunk, i, arr) => {
    const suffix = arr.length > 1
      ? ` ${translate("insights.report.continued", { defaultValue: "(continuación)" })}`
      : "";
    const title = i === 0
      ? translate("insights.report.sections.conclusion", { defaultValue: "Conclusión" })
      : `${translate("insights.report.sections.conclusion", { defaultValue: "Conclusión" })}${suffix}`;
    return `${buildRunheadHtml(name, callsAnalyzed, hasSatisfaction, counts, translate)}
    <div class="sec">
      <div class="sec__mark"></div>
      <h2 class="sec__head">${escapeHtml(title)}</h2>
      <p class="prose prose--lead">${escapeHtml(chunk)}</p>
    </div>`;
  });

  // ------------------------------------------------------------------
  // Páginas de acción — aspectos a mejorar + recomendaciones.
  // ------------------------------------------------------------------
  const actionChunks = paginateActionSequence(aspectsToImprove, recommendations);
  const seenActionTypes = new Set();
  const recCounterRef = { value: 0 };
  const actionPages = actionChunks.map((pageItems) => {
    const inner = buildActionPageInnerHtml(pageItems, seenActionTypes, recCounterRef, translate);
    return `${buildRunheadHtml(name, callsAnalyzed, hasSatisfaction, counts, translate)}
    ${inner}`;
  });

  // ------------------------------------------------------------------
  // Última página — emociones + nota metodológica.
  // ------------------------------------------------------------------
  const emotionsHtml = buildEmotionsSectionHtml(emotionsRanking, callsAnalyzed, translate);
  const notesHtml = `<div class="sec">
    <div class="sec__mark"></div>
    <h2 class="sec__head">${escapeHtml(translate("insights.report.sections.howToRead", { defaultValue: "Cómo leer este informe" }))}</h2>

    <div class="note">
      <div class="note__key">${escapeHtml(translate("insights.report.notes.builtKey", { defaultValue: "Cómo se construyó" }))}</div>
      <p class="note__text">${escapeHtml(translate("insights.report.notes.builtText", { defaultValue: "Las métricas de satisfacción y de emociones se calculan de forma determinística sobre las interacciones del período: cada interacción aporta un valor y el informe agrega los totales, sin intervención de modelos de lenguaje. La conclusión, los aspectos a mejorar y las recomendaciones los redacta un modelo de lenguaje a partir del análisis de esas mismas interacciones." }))}</p>
    </div>

    <div class="note">
      <div class="note__key">${escapeHtml(translate("insights.report.notes.missingKey", { defaultValue: "Tratamiento de los casos sin dato" }))}</div>
      <p class="note__text">${escapeHtml(translate("insights.report.notes.missingText", { defaultValue: "Las interacciones que no tienen análisis de experiencia disponible se informan como \u00absin dato\u00bb y no se reparten entre las otras categorías. Los porcentajes de satisfacción se calculan sobre el total de interacciones del período, incluidas esas." }))}</p>
    </div>

    <div class="note">
      <div class="note__key">${escapeHtml(translate("insights.report.notes.scopeKey", { defaultValue: "Alcance de la lectura automática" }))}</div>
      <p class="note__text">${escapeHtml(translate("insights.report.notes.scopeText", { defaultValue: "El texto redactado por el modelo puede contener imprecisiones y no reemplaza la revisión de un supervisor. Las decisiones sobre personas o procesos conviene apoyarlas también en la escucha directa de las interacciones. Este informe describe un conjunto y un período: no constituye una evaluación individual de desempeño." }))}</p>
    </div>
  </div>`;

  const lastPageInner = `${buildRunheadHtml(name, callsAnalyzed, hasSatisfaction, counts, translate)}
  ${emotionsHtml}
  ${notesHtml}`;

  // ------------------------------------------------------------------
  // Ensamblado final: numeración real, no hardcodeada.
  // ------------------------------------------------------------------
  const pageInnersInOrder = [page1Inner, ...conclusionOverflowPages, ...actionPages, lastPageInner];
  const totalPages = pageInnersInOrder.length;
  const sheetsHtml = pageInnersInOrder
    .map((inner, i) => wrapSheet(inner, i + 1, totalPages, translate))
    .join("\n\n");

  const titleText = `${translate("insights.report.pdfTitlePrefix", { defaultValue: "Informe CX" })} - ${sanitizeFilenamePart(name)} - ${sanitizeFilenamePart(periodShort)}`;

  return `<!doctype html>
<html lang="${translate("insights.report.htmlLang", { defaultValue: "es" })}">
<head>
<meta charset="utf-8">
<title>${escapeHtml(titleText)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap" rel="stylesheet">
<style>${REPORT_CSS}</style>
</head>
<body>
${sheetsHtml}
</body>
</html>`;
}
